/**
 * localStorage helpers for tour dismissal state.
 *
 * - Dismissed flag: written when the user completes/skips the tour. Survives
 *   PATCH failures so a flaky network can't cause the tour to replay across
 *   sessions. The version is part of the key so bumping CURRENT_TOUR_VERSION
 *   naturally invalidates older flags.
 * - Reminder counter: incremented when the user clicks "Remind me later"
 *   (per-version). The button is hidden once the counter reaches the limit;
 *   that limit is enforced by the consumer, not this module.
 *
 * All accessors are wrapped in try/catch so Safari private mode (and similar
 * environments where storage access throws) can't break the tour.
 */

export const dismissedKey = (version: number): string =>
  `lr.onboarding.dismissed.v${version}`;

export const reminderKey = (version: number): string =>
  `lr.onboarding.reminder_count.v${version}`;

export const isDismissed = (version: number): boolean => {
  try {
    return window.localStorage.getItem(dismissedKey(version)) === '1';
  } catch {
    return false;
  }
};

export const setDismissed = (version: number): void => {
  try {
    window.localStorage.setItem(dismissedKey(version), '1');
  } catch {
    // Best-effort. Optimistic in-memory update still prevents replay this
    // session even if storage is denied.
  }
};

export const clearDismissed = (version: number): void => {
  try {
    window.localStorage.removeItem(dismissedKey(version));
  } catch {
    // Best-effort.
  }
};

export const getReminderCount = (version: number): number => {
  try {
    const raw = window.localStorage.getItem(reminderKey(version));
    if (raw == null) return 0;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  } catch {
    return 0;
  }
};

export const incrementReminderCount = (version: number): number => {
  const next = getReminderCount(version) + 1;
  try {
    window.localStorage.setItem(reminderKey(version), String(next));
  } catch {
    // Best-effort. The in-memory counter still reflects the new value for
    // this session.
  }
  return next;
};

export const clearReminderCount = (version: number): void => {
  try {
    window.localStorage.removeItem(reminderKey(version));
  } catch {
    // Best-effort.
  }
};
